import { COMPANY, INDUSTRIES, PRODUCT_CATEGORIES, MARKETS, CERTIFICATIONS } from "../lib/content";
import { Download, Printer, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function CompanyProfile() {
  const year = new Date().getFullYear();

  return (
    <div className="bg-white" data-testid="company-profile-page">
      {/* Toolbar - hidden in print */}
      <div className="no-print sticky top-20 z-10 bg-white border-b border-[#E3E6EC]">
        <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#4B5668] hover:text-[#0A1F3F]">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <div className="flex gap-3">
            <button
              onClick={() => window.print()}
              data-testid="profile-print-button"
              className="inline-flex items-center gap-2 bg-[#0A1F3F] text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#15325F]"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
            <button
              onClick={() => window.print()}
              data-testid="profile-download-button"
              className="inline-flex items-center gap-2 bg-[#F37021] text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#D35F16]"
            >
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 print:py-8">
        {/* Cover */}
        <section className="border-b border-[#E3E6EC] pb-12 mb-12">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-14 h-14 bg-[#0A1F3F] text-white flex items-center justify-center font-display font-black text-xl border-b-4 border-[#F37021]">
              B
            </div>
            <div>
              <div className="font-display font-black text-2xl tracking-tight text-[#0A1F3F]">
                BNM GROUP
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#4B5668] mt-1">
                {COMPANY.tagline}
              </div>
            </div>
          </div>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl text-[#0A1F3F] leading-[0.98]">
            Company Profile {year}
          </h1>
          <p className="mt-6 text-[#4B5668] max-w-2xl leading-relaxed">
            International trading and industrial supply across petrochemicals,
            industrial machinery, transportation systems, agricultural
            commodities, and telecommunications infrastructure.
          </p>
        </section>

        {/* Introduction */}
        <Section title="Company Introduction" num="01">
          <p>
            BNM Group is an international trading and industrial supply company
            headquartered in Ankara, Turkey.
          </p>
          <p>
            The company operates across multiple sectors including petrochemical
            products, industrial machinery, transportation systems, agricultural
            commodities, telecommunications infrastructure, and global sourcing
            services.
          </p>
          <p>
            With a strong network of international suppliers, manufacturers, and
            logistics partners, BNM Group facilitates reliable and efficient trade
            between global markets.
          </p>
          <p className="font-display font-bold text-[#0A1F3F]">
            Mission: To connect industries, markets, and opportunities through
            strategic global partnerships.
          </p>
        </Section>

        {/* Industries */}
        <Section title="Industries" num="02">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 not-prose">
            {INDUSTRIES.map((i) => (
              <div key={i.id} className="border border-[#E3E6EC] p-5">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#F37021]">
                  {i.number}
                </div>
                <div className="font-display font-black text-xl text-[#0A1F3F] mt-1">
                  {i.title}
                </div>
                <p className="text-sm text-[#4B5668] mt-2 leading-relaxed">
                  {i.description}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* Products */}
        <Section title="Products" num="03">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 not-prose">
            {PRODUCT_CATEGORIES.map((p) => (
              <div key={p.id} className="border border-[#E3E6EC] p-5">
                <div className="font-display font-black text-lg text-[#0A1F3F]">
                  {p.title}
                </div>
                <ul className="mt-2 text-sm text-[#4B5668] space-y-1">
                  {p.items.map((it) => (
                    <li key={it}>• {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* Markets */}
        <Section title="Global Markets" num="04">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 not-prose">
            {MARKETS.map((m) => (
              <div key={m.id} className="border border-[#E3E6EC] p-5">
                <div className="font-display font-black text-lg text-[#0A1F3F]">
                  {m.name}
                </div>
                <p className="text-sm text-[#4B5668] mt-2 leading-relaxed">
                  {m.summary}
                </p>
                <div className="text-xs text-[#4B5668] uppercase tracking-[0.15em] mt-3">
                  {m.countries.join(" · ")}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Certifications */}
        <Section title="Standards &amp; Compliance" num="05">
          <ul className="space-y-3">
            {CERTIFICATIONS.map((c) => (
              <li key={c.title}>
                <span className="font-display font-bold text-[#0A1F3F]">{c.title}.</span>{" "}
                <span className="text-[#4B5668]">{c.body}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Contact */}
        <Section title="Contact Information" num="06">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 not-prose">
            <div>
              <div className="text-eyebrow text-[#F37021] mb-2">Company</div>
              <div className="text-[#0A1F3F] font-semibold">{COMPANY.name}</div>
            </div>
            <div>
              <div className="text-eyebrow text-[#F37021] mb-2">Address</div>
              <div className="text-[#0A1F3F]">{COMPANY.address}</div>
            </div>
            <div>
              <div className="text-eyebrow text-[#F37021] mb-2">Phone</div>
              <div className="text-[#0A1F3F]">{COMPANY.phone}</div>
            </div>
            <div>
              <div className="text-eyebrow text-[#F37021] mb-2">Email</div>
              <div className="text-[#0A1F3F]">{COMPANY.email}</div>
            </div>
          </div>
        </Section>

        <div className="mt-16 pt-8 border-t border-[#E3E6EC] text-center text-xs text-[#4B5668] uppercase tracking-[0.22em]">
          © {year} BNM Group · Ankara · Dubai · Almaty · Lagos
        </div>
      </div>
    </div>
  );
}

function Section({ title, num, children }) {
  return (
    <section className="mb-14">
      <div className="flex items-center gap-3 mb-5">
        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F37021]">
          Section {num}
        </span>
      </div>
      <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-[#0A1F3F] mb-6">
        {title}
      </h2>
      <div className="prose max-w-none text-[#0A1F3F] space-y-4 leading-relaxed">
        {children}
      </div>
    </section>
  );
}
